/**
 * Case Questions on the case-detail panel (#613, ADR-0150) — every OF/LF
 * question→answer pair on this matter, read through the CC worker's proxy. CC
 * sees the ADMIN shape (real names). This is the ONLY LF-ask door: a Placed-and-
 * beyond matter shows "Ask the handling firm", hidden while an LF question is
 * already open (the portal 409s on a second).
 *
 * A Case Question is a one-time Q→A, not a thread — the row renders the whole
 * pair inline. Distinct from the Messages section (firm↔LF conversations).
 */

import { useState } from "react";
import { canAskCaseQuestion, type CaseQuestion } from "@command-center/shared";
import { useCaseQuestionsForLead } from "@/lib/use-case-questions";
import { sortCaseQuestions } from "@/components/case-questions-lane";
import { AskCaseQuestionModal } from "@/components/ask-case-question-modal";
import { Button } from "@/components/ui/button";

export function CaseQuestions({
  leadId,
  placementId,
  decision,
  clientName,
  testId = "case-questions",
}: {
  leadId: string;
  placementId: string | null;
  decision: string | null;
  clientName: string;
  testId?: string;
}) {
  const [askOpen, setAskOpen] = useState(false);
  const questions = useCaseQuestionsForLead(leadId, true);

  if (questions.isLoading) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-loading`}>
        Loading questions…
      </p>
    );
  }
  if (questions.isError) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-error`}>
        Couldn't load case questions. Close and reopen the case to retry.
      </p>
    );
  }

  const list = sortCaseQuestions(questions.data?.questions ?? []);
  const hasOpenLf = list.some((q) => q.asker_role === "LF" && q.status === "Open");
  // LF may ask when the matter is placed AND LF has no open question here.
  const canAsk = placementId !== null && canAskCaseQuestion(decision) && !hasOpenLf;

  return (
    <div data-testid={testId}>
      {list.length === 0 ? (
        <p className="text-sm italic text-muted-foreground" data-testid={`${testId}-empty`}>
          No case questions on this matter.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid={`${testId}-list`}>
          {list.map((q) => (
            <PanelQuestionRow key={q.id} q={q} testId={testId} />
          ))}
        </ul>
      )}

      {canAsk && placementId !== null ? (
        <div className="mt-3">
          <Button variant="outline" onClick={() => setAskOpen(true)} data-testid={`${testId}-ask`}>
            Ask the handling firm
          </Button>
        </div>
      ) : null}

      {placementId !== null ? (
        <AskCaseQuestionModal
          open={askOpen}
          onClose={() => setAskOpen(false)}
          placementId={placementId}
          clientName={clientName}
        />
      ) : null}
    </div>
  );
}

function PanelQuestionRow({ q, testId }: { q: CaseQuestion; testId: string }) {
  const answered = q.status === "Answered";
  // Real names in CC (ADMIN shape). LF asks name "LegalFlare"; OF asks name the
  // contact who asked.
  const asker = q.asker_role === "LF" ? "LegalFlare" : q.acting_name || q.of_firm_name || "Originating firm";
  return (
    <li
      className="rounded-md border border-border p-2.5 text-sm"
      data-testid={`${testId}-row`}
      data-status={q.status}
    >
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="font-medium">
          {asker}
          <span className="ml-1 text-xs font-normal text-plane-muted">({q.asker_role})</span>
        </span>
        <span
          className={
            answered
              ? "rounded-full bg-plane-muted/15 px-2 py-0.5 text-[10px] font-semibold text-plane-muted"
              : "rounded-full bg-active-edge/15 px-2 py-0.5 text-[10px] font-semibold text-active-fg"
          }
        >
          {answered ? "Answered" : "Awaiting answer"}
        </span>
      </div>
      <div>{q.question}</div>
      {q.note ? <div className="mt-0.5 text-plane-muted">“{q.note}”</div> : null}
      {answered ? (
        <div className="mt-2 border-l-2 border-active-edge/40 pl-2">
          <span className="font-medium">{q.answered_by_name || q.hf_firm_name || "Handling firm"}</span>
          <span className="text-plane-muted"> answered</span>
          <div>{q.answer}</div>
        </div>
      ) : null}
    </li>
  );
}
